import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  CreateTestModuleRequest,
  EmbeddedModule,
  SaveFullTestModuleRequest,
  SaveFullTestModuleResponse,
  TestModule,
  TestModuleDetail,
  TestModuleUsage,
  UpdateTestModuleRequest,
} from '../models/test-module.model';

@Injectable({ providedIn: 'root' })
export class TestModuleService {
  private http = inject(HttpClient);
  private base = `${environment.apiBaseUrl}/api/v1/projects`;

  private moduleUrl(projectId: string, moduleId?: string): string {
    const root = `${this.base}/${projectId}/test-modules`;
    return moduleId ? `${root}/${moduleId}` : root;
  }

  list(projectId: string): Observable<TestModule[]> {
    return this.http.get<TestModule[]>(this.moduleUrl(projectId));
  }

  getById(projectId: string, moduleId: string): Observable<TestModuleDetail> {
    return this.http.get<TestModuleDetail>(this.moduleUrl(projectId, moduleId));
  }

  listUsage(projectId: string, moduleId: string): Observable<TestModuleUsage[]> {
    return this.http.get<TestModuleUsage[]>(`${this.moduleUrl(projectId, moduleId)}/usage`);
  }

  create(projectId: string, request: CreateTestModuleRequest): Observable<TestModule> {
    return this.http.post<TestModule>(this.moduleUrl(projectId), request);
  }

  update(projectId: string, moduleId: string, request: UpdateTestModuleRequest): Observable<TestModule> {
    return this.http.patch<TestModule>(this.moduleUrl(projectId, moduleId), request);
  }

  delete(projectId: string, moduleId: string): Observable<void> {
    return this.http.delete<void>(this.moduleUrl(projectId, moduleId));
  }

  createFull(projectId: string, request: SaveFullTestModuleRequest): Observable<SaveFullTestModuleResponse> {
    return this.http.post<SaveFullTestModuleResponse>(`${this.moduleUrl(projectId)}/full`, request);
  }

  saveFull(
    projectId: string,
    moduleId: string,
    request: SaveFullTestModuleRequest,
  ): Observable<SaveFullTestModuleResponse> {
    return this.http.put<SaveFullTestModuleResponse>(`${this.moduleUrl(projectId, moduleId)}/full`, request);
  }

  addEmbed(projectId: string, moduleId: string, embeddedModuleId: string): Observable<EmbeddedModule> {
    return this.http.post<EmbeddedModule>(`${this.moduleUrl(projectId, moduleId)}/embeds`, {
      embeddedModuleId,
    });
  }

  removeEmbed(projectId: string, moduleId: string, embedId: string): Observable<void> {
    return this.http.delete<void>(`${this.moduleUrl(projectId, moduleId)}/embeds/${embedId}`);
  }
}
